import React, { useEffect, useState, useMemo } from "react";
import { styled } from "@mui/material/styles";
import {
    Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead,
    TableRow, Paper, FormControl, Select, MenuItem, InputLabel, Box, Typography,
    CircularProgress, Chip
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchTransactionsBySeller } from "../../../State/seller/transactionSlice";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

export default function TransactionTable() {
    const dispatch = useAppDispatch();
    const { transactions, loading, error } = useAppSelector((state) => state.transactions);
    const [timeRange, setTimeRange] = useState<string>("24");

    useEffect(() => {
        const jwt = localStorage.getItem("jwt") || "";
        if (jwt) {
            dispatch(fetchTransactionsBySeller(jwt));
        }
    }, [dispatch]);

    const filteredTransactions = useMemo(() => {
        if (!transactions) return [];

        const now = new Date().getTime();

        return [...transactions]
            .filter((item) => {
                if (timeRange === "ALL") return true;

                const txDate = new Date(item.date).getTime();
                const hoursDiff = (now - txDate) / (1000 * 60 * 60);

                if (timeRange === "24") return hoursDiff <= 24;
                if (timeRange === "168") return hoursDiff <= 168;
                if (timeRange === "720") return hoursDiff <= 720;

                return true;
            })
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [transactions, timeRange]);

    return (
        <Box className="space-y-4">
            <Paper className="p-4 flex justify-between items-center flex-wrap gap-4">
                <div>
                    <Typography variant="h6" className="font-bold">Seller Transactions</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Showing {filteredTransactions.length} transaction(s)
                    </Typography>
                </div>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="time-range-label">Time Range</InputLabel>
                    <Select
                        labelId="time-range-label"
                        value={timeRange}
                        label="Time Range"
                        onChange={(e) => setTimeRange(e.target.value)}
                    >
                        <MenuItem value="24">Last 24 Hours</MenuItem>
                        <MenuItem value="168">Last 7 Days</MenuItem>
                        <MenuItem value="720">Last 30 Days</MenuItem>
                        <MenuItem value="ALL">All Time</MenuItem>
                    </Select>
                </FormControl>
            </Paper>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 700 }} aria-label="seller transactions table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Transaction ID (M-Pesa)</StyledTableCell>
                            <StyledTableCell>Date & Time</StyledTableCell>
                            <StyledTableCell align="left">Customer Email</StyledTableCell>
                            <StyledTableCell align="center">Order ID</StyledTableCell>
                            <StyledTableCell align="center">Payment Status</StyledTableCell>
                            <StyledTableCell align="right">Amount</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={6} align="center" className="py-8">
                                    <CircularProgress size={28} />
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : error ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={6} align="center" className="py-8 text-red-500">
                                    {error}
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : filteredTransactions.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={6} align="center" className="py-8 text-gray-500">
                                    No transactions found for the selected time window.
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : (
                            filteredTransactions.map((item) => {
                                const mpesaReceipt =
                                    item.order?.paymentDetails?.mpesaReceiptNumber ||
                                    item.order?.paymentDetails?.paymentId ||
                                    `TXN-${item.id}`;

                                const paymentStatus =
                                    item.paymentStatus ||
                                    item.order?.paymentDetails?.paymentStatus ||
                                    item.order?.paymentStatus ||
                                    "PENDING";

                                const isCompleted = paymentStatus.toUpperCase() === "COMPLETED" || paymentStatus.toUpperCase() === "SUCCESS";

                                return (
                                    <StyledTableRow key={item.id}>
                                        <StyledTableCell scope="row" className="font-medium">
                                            {mpesaReceipt}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {new Date(item.date).toLocaleString()}
                                        </StyledTableCell>
                                        <StyledTableCell align="left">
                                            {item.customer?.email || "N/A"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {item.order?.id ? `#${item.order.id}` : "N/A"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Chip
                                                label={paymentStatus}
                                                color={isCompleted ? "success" : "warning"}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="right" className="font-semibold">
                                            KES {item.order?.totalSellingPrice !== undefined
                                                ? item.order.totalSellingPrice.toLocaleString()
                                                : "0.00"}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}